import React, { Suspense, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import StockParticle from './StockParticle';
import StockTrail from './StockTrail';
import TrajectoryVertices from './TrajectoryVertices';
import TimelinePlayer from '../timeline/TimelinePlayer';
import { useStockData } from '../../hooks/useStockData';
import { useDraggable } from '../../hooks/useDraggable';
import './StockUniverse.css';

/**
 * Main 3D visualization component - renders all stocks in embedding space
 * Features:
 * - 50 stocks as particles in 3D space
 * - Industry clustering with color coding
 * - Wireframe cluster visualization
 * - Interactive camera controls
 * - Hover and click interactions
 */

/**
 * CameraController - Simple pan-only control for fixed 2D view
 * Left drag: Pan the view (move left/right/up/down)
 * Fixed orthographic camera - no rotation or zoom confusion
 * Initial orientation: Looking straight at the XY plane to see price movement clearly
 */
const CameraController = () => {
  const { camera } = useThree();
  const controlsRef = useRef({
    isPanning: false,
    previousMouse: { x: 0, y: 0 },
    panOffset: { x: 0, y: 0 },
  });

  useEffect(() => {
    const onMouseDown = (e) => {
      if (e.button === 0) {
        // Left click to pan
        controlsRef.current.isPanning = true;
        controlsRef.current.previousMouse = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseMove = (e) => {
      if (controlsRef.current.isPanning) {
        const deltaY = e.clientY - controlsRef.current.previousMouse.y;

        // Pan only vertically (Y axis)
        const scale = 0.02; // Sensitivity
        controlsRef.current.panOffset.y += deltaY * scale;

        controlsRef.current.previousMouse = { x: e.clientX, y: e.clientY };
      }
    };

    const onMouseUp = () => {
      controlsRef.current.isPanning = false;
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [camera]);

  useFrame(() => {
    // Update camera position with pan offset
    // Position camera to look straight at XY plane (perpendicular view)
    camera.position.x = controlsRef.current.panOffset.x;
    camera.position.y = controlsRef.current.panOffset.y;
    camera.position.z = 5; // Very close to XY plane for clear 2D view
    camera.lookAt(controlsRef.current.panOffset.x, controlsRef.current.panOffset.y, 0);
  });

  return null;
};

const StockUniverse = ({ selectedStocks, onBack }) => {
  const [hoveredStock, setHoveredStock] = useState(null);
  const [internalSelectedStock, setInternalSelectedStock] = useState(null);
  const [currentTimeStep, setCurrentTimeStep] = useState(0);
  const [showTrails, setShowTrails] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('3m');
  const [predictions, setPredictions] = useState({});
  const [isAutoProgressing, setIsAutoProgressing] = useState(false);
  const [autoProgressEnabled, setAutoProgressEnabled] = useState(false);
  const [selectedPredictionStep, setSelectedPredictionStep] = useState(null);
  const [requestedPredictionStep, setRequestedPredictionStep] = useState(null);
  const [predictionCount, setPredictionCount] = useState(0);
  const [bundleAnalysis, setBundleAnalysis] = useState(null);
  const [analyzingBundle, setAnalyzingBundle] = useState(false);

  const { stocks: liveStocks, loading, error } = useStockData(selectedTimeframe, true);

  // Match selectedStocks with liveStocks (which have trajectories) by symbol
  // Preserve quantity from user selection when merging with live data
  const enrichedSelectedStocks = selectedStocks?.map(selected => {
    const liveStock = liveStocks.find(s => s.symbol === selected.symbol);
    if (liveStock) {
      return { ...liveStock, quantity: selected.quantity || 1 };
    }
    return selected;
  }) || [];
  const totalTimeSteps = 100;

  const timeframeOptions = [
    { label: '1W', value: '1w' },
    { label: '1M', value: '1m' },
    { label: '3M', value: '3m' },
    { label: '6M', value: '6m' },
  ];


  // Create bundle from selected stocks with weighted averaging
  const createBundle = (stocks) => {
    if (!stocks || stocks.length === 0) return null;
    if (stocks.length === 1) return stocks[0];

    // Wait until all stocks have trajectories before building bundle
    const allStocksReady = stocks.every(s => s.trajectory && s.trajectory.length > 0);
    if (!allStocksReady) return null;

    const trajectoryLength = stocks[0].trajectory?.length || 30;

    // Calculate total weight
    const totalWeight = stocks.reduce((sum, s) => sum + (s.quantity || 1), 0);

    // First pass: calculate weighted bundle prices to determine min/max for Y-axis calibration
    const bundlePrices = [];
    for (let step = 0; step < trajectoryLength; step++) {
      let weightedSumPrice = 0;
      let weightSum = 0;

      stocks.forEach(stock => {
        if (stock.trajectory && stock.trajectory[step]) {
          const point = stock.trajectory[step];
          const weight = stock.quantity || 1;
          weightedSumPrice += point.price * weight;
          weightSum += weight;
        }
      });

      if (weightSum > 0) {
        bundlePrices.push(weightedSumPrice / weightSum);
      }
    }

    // Calculate min/max from actual bundle prices
    const MIN_PRICE = Math.min(...bundlePrices);
    const MAX_PRICE = Math.max(...bundlePrices);
    const priceRange = MAX_PRICE - MIN_PRICE;

    const bundleTrajectory = [];

    for (let step = 0; step < trajectoryLength; step++) {
      let weightedSumPrice = 0;
      let weightedSumMomentum = 0;
      let weightedSumVolatility = 0;
      let weightedSumVolume = 0;
      let weightedSumX = 0;
      let weightSum = 0;

      stocks.forEach(stock => {
        if (stock.trajectory && stock.trajectory[step]) {
          const point = stock.trajectory[step];
          const weight = stock.quantity || 1;
          weightedSumPrice += point.price * weight;
          weightedSumMomentum += point.momentum * weight;
          weightedSumVolatility += point.volatility * weight;
          weightedSumVolume += point.volume * weight;
          weightedSumX += point.x * weight;
          weightSum += weight;
        }
      });

      if (weightSum > 0) {
        const weightedPrice = weightedSumPrice / weightSum;
        // Map price to Y coordinate: scale based on actual price range
        // Add 10% padding above/below min/max for visual breathing room
        const padding = priceRange * 0.1;
        const scaledMin = MIN_PRICE - padding;
        const scaledMax = MAX_PRICE + padding;
        const scaledRange = scaledMax - scaledMin;
        const calibratedY = ((weightedPrice - scaledMin) / scaledRange) * 10 - 5;

        const weightedMomentum = weightedSumMomentum / weightSum;
        bundleTrajectory.push({
          x: weightedSumX / weightSum,
          y: calibratedY,
          z: 0,
          price: weightedPrice,
          priceChange: (weightedPrice / bundleTrajectory[step - 1]?.price - 1) * 100 || 0,
          momentum: weightedMomentum,
          volatility: weightedSumVolatility / weightSum,
          volume: weightedSumVolume,
          timestamp: stocks[0].trajectory[step].timestamp,
        });
      }
    }

    // Calculate up/down probability from momentum values
    const momentumValues = bundleTrajectory.map(p => p.momentum);
    const upCount = momentumValues.filter(m => m > 0).length;
    const downCount = momentumValues.filter(m => m < 0).length;
    const totalCount = upCount + downCount;
    const probUp = totalCount > 0 ? (upCount / totalCount) * 100 : 50;
    const probDown = totalCount > 0 ? (downCount / totalCount) * 100 : 50;

    // Calculate performance score (0-100)
    const minMomentum = Math.min(...momentumValues);
    const maxMomentum = Math.max(...momentumValues);
    const momentumRange = Math.abs(maxMomentum - minMomentum);
    const avgVolatility = bundleTrajectory.reduce((sum, p) => sum + p.volatility, 0) / bundleTrajectory.length;

    // Score components
    const upProbScore = probUp;
    const volatilityScore = Math.max(0, 100 - (avgVolatility * 500));
    const consistencyScore = Math.max(0, 100 - (momentumRange * 100));

    // Weighted average score
    const performanceScore = (upProbScore * 0.5 + volatilityScore * 0.3 + consistencyScore * 0.2);

    // Build bundle name with quantities
    const bundleName = stocks
      .map(s => `${s.quantity || 1}x ${s.symbol}`)
      .join(' + ');

    // Store debug info in window
    if (typeof window !== 'undefined') {
      window.bundleDebug = {
        stocks: stocks.map(s => `${s.quantity || 1}x ${s.symbol}`),
        weights: stocks.map(s => s.quantity || 1),
        momentumValues: momentumValues.map(m => parseFloat(m.toFixed(6))),
        minMomentum: parseFloat(minMomentum.toFixed(6)),
        maxMomentum: parseFloat(maxMomentum.toFixed(6)),
        momentumRange: parseFloat(momentumRange.toFixed(6)),
        probUp: parseFloat(probUp.toFixed(1)),
        probDown: parseFloat(probDown.toFixed(1)),
        avgVolatility: parseFloat(avgVolatility.toFixed(4)),
        performanceScore: parseFloat(performanceScore.toFixed(1)),
      };
    }

    return {
      id: 'bundle',
      symbol: `BUNDLE`,
      name: bundleName,
      color: '#00ffff',
      industry: 'Portfolio Bundle',
      trajectory: bundleTrajectory,
      probUp: probUp,
      probDown: probDown,
      performanceScore: performanceScore,
      price: 0,
      priceChange: 0,
      momentum: 0,
      volatility: 0,
      volume: 0,
    };
  };

  const bundle = createBundle(enrichedSelectedStocks);
  const displayStocks = bundle ? [bundle] : [];

  // Fetch bundle analysis from API when bundle changes
  useEffect(() => {
    if (!bundle || !enrichedSelectedStocks || enrichedSelectedStocks.length === 0) {
      setBundleAnalysis(null);
      return;
    }

    const fetchBundleAnalysis = async () => {
      setAnalyzingBundle(true);
      try {
        const response = await fetch('http://localhost:5000/api/bundle-analysis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            stocks: enrichedSelectedStocks.map(s => ({
              symbol: s.symbol,
              name: s.name,
              quantity: s.quantity || 1,
            })),
            volatility: bundle.volatility || 0,
            momentum: bundle.momentum || 0,
            performanceScore: bundle.performanceScore || 0,
          }),
        });

        if (response.ok) {
          const analysis = await response.json();
          setBundleAnalysis(analysis);
        } else {
          console.error('Failed to fetch bundle analysis:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching bundle analysis:', error);
      } finally {
        setAnalyzingBundle(false);
      }
    };

    fetchBundleAnalysis();
  }, [bundle, enrichedSelectedStocks]);

  // Calculate actual price range from bundle trajectory for Y-axis calibration
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  if (bundle && bundle.trajectory) {
    bundle.trajectory.forEach(point => {
      minPrice = Math.min(minPrice, point.price);
      maxPrice = Math.max(maxPrice, point.price);
    });
  }
  const validPriceRange = minPrice !== Infinity && maxPrice !== -Infinity;

  // Helper to get stock data at current time step
  const getStockAtTimeStep = (stock, timeStep) => {
    if (!stock) return null;

    if (stock.trajectory && stock.trajectory[timeStep]) {
      const point = stock.trajectory[timeStep];
      return {
        ...stock,
        x: point.x,
        y: point.y,
        z: point.z,
        price: point.price,
        priceChange: point.priceChange,
        momentum: point.momentum,
        volatility: point.volatility,
        volume: point.volume,
        timestamp: point.timestamp,
      };
    }

    // Return stock with safe defaults if trajectory not available
    return {
      ...stock,
      x: stock.x || 0,
      y: stock.y || 0,
      z: stock.z || 0,
      price: stock.price || 0,
      priceChange: stock.priceChange || 0,
      momentum: stock.momentum || 0,
      volatility: stock.volatility || 0,
      volume: stock.volume || 0,
      timestamp: stock.timestamp || new Date().toISOString(),
    };
  };

  // Update hovered/selected stock data when timeline changes
  const updatedHoveredStock = hoveredStock ? getStockAtTimeStep(hoveredStock, currentTimeStep) : null;
  const updatedBundleData = bundle ? getStockAtTimeStep(bundle, currentTimeStep) : null;

  // Get single prediction when vertex is clicked (non-progressing mode)
  useEffect(() => {
    if (requestedPredictionStep === null || !bundle) {
      return;
    }

    const stepIndex = requestedPredictionStep;
    const predictionDaysOut = 2; // Predict 2 days out
    const targetStep = stepIndex + predictionDaysOut;

    // Don't predict beyond available data
    if (targetStep >= totalTimeSteps) {
      setRequestedPredictionStep(null);
      return;
    }

    // If we already have this prediction, just select it and clear the request
    if (predictions[stepIndex]) {
      setSelectedPredictionStep(stepIndex);
      setRequestedPredictionStep(null);
      return;
    }

    const getPrediction = async () => {
      try {
        const currentData = bundle.trajectory[stepIndex];
        const targetData = bundle.trajectory[targetStep];
        if (!currentData || !targetData) return;

        // Only use data up to and including current step
        const currentPrice = currentData.price;
        const priceHistory = bundle.trajectory.slice(0, stepIndex + 1).map(d => d.price);
        const momentumHistory = bundle.trajectory.slice(0, stepIndex + 1).map(d => d.momentum);

        // Calculate trend from available data
        const priceChange = stepIndex > 0 ? ((currentPrice - bundle.trajectory[0].price) / bundle.trajectory[0].price * 100) : 0;
        const avgMomentum = momentumHistory.reduce((a, b) => a + b, 0) / momentumHistory.length;

        // Prepare stats for LLM
        const statsPrompt = `
Current Bundle Stats (up to this point in time):
- Current Price: $${currentPrice.toFixed(2)}
- Overall Price Change: ${priceChange.toFixed(2)}%
- Current Momentum: ${(currentData.momentum * 100).toFixed(2)}%
- Average Momentum (up to now): ${(avgMomentum * 100).toFixed(2)}%
- Current Volatility: ${(currentData.volatility * 100).toFixed(2)}%
- Performance Score: ${bundle.performanceScore.toFixed(1)}/100

Based ONLY on the information available up to this point (you cannot see future data), predict if the price ${predictionDaysOut} days from now will be UP or DOWN compared to today. Respond with only one word: UP or DOWN`;

        const response = await fetch('/api/predict', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: statsPrompt, withReasoning: true }),
        });

        if (!response.ok) throw new Error('Prediction failed');
        const result = await response.json();
        const prediction = result.prediction?.toUpperCase() || 'UP';
        const reasoning = result.reasoning || 'No reasoning provided';

        // Store prediction with target step metadata
        setPredictions(prev => ({
          ...prev,
          [stepIndex]: {
            prediction,
            reasoning,
            actual: null,
            correct: null,
            targetStep: targetStep,
            predictionDaysOut: predictionDaysOut,
          },
        }));

        // Select this prediction to show details immediately
        setSelectedPredictionStep(stepIndex);

        // Increment prediction count
        setPredictionCount(prev => prev + 1);
      } catch (err) {
        console.error('Single prediction error:', err);
      } finally {
        setRequestedPredictionStep(null);
      }
    };

    getPrediction();
  }, [requestedPredictionStep, bundle, predictions, totalTimeSteps]);

  // Get LLM prediction for 2 days out (only using info available up to current tick)
  // Predictions stop after 2 predictions are made
  useEffect(() => {
    const predictionDaysOut = 2;
    const maxPredictionCount = 2; // Only make 2 predictions total

    if (!autoProgressEnabled || !bundle || isAutoProgressing) {
      return;
    }

    // Stop if we've already made 2 predictions
    if (predictionCount >= maxPredictionCount) {
      setAutoProgressEnabled(false);
      return;
    }

    // Stop if we can't predict 2 days out from current position
    if (currentTimeStep + predictionDaysOut >= totalTimeSteps) {
      setAutoProgressEnabled(false);
      return;
    }

    // If we already have a prediction for this step, auto-progress and check outcome
    if (predictions[currentTimeStep]) {
      const targetStep = predictions[currentTimeStep].targetStep || (currentTimeStep + predictionDaysOut);
      const targetData = bundle.trajectory[targetStep];
      const currentData = bundle.trajectory[currentTimeStep];

      if (targetData && currentData) {
        const actualDirection = targetData.price > currentData.price ? 'UP' : 'DOWN';
        const pred = predictions[currentTimeStep];
        const isCorrect = pred.prediction === actualDirection;

        // Update with actual result
        setPredictions(prev => ({
          ...prev,
          [currentTimeStep]: {
            ...pred,
            actual: actualDirection,
            correct: isCorrect,
          },
        }));
      }

      // Auto-progress after 2 seconds
      setIsAutoProgressing(true);
      setTimeout(() => {
        if (currentTimeStep < totalTimeSteps - 1) {
          setCurrentTimeStep(currentTimeStep + 1);
        }
        setIsAutoProgressing(false);
      }, 2000);
      return;
    }

    const getPrediction = async () => {
      try {
        const currentData = bundle.trajectory[currentTimeStep];
        const targetStep = currentTimeStep + predictionDaysOut;
        const targetData = bundle.trajectory[targetStep];

        if (!currentData || !targetData) return;

        // Only use data up to and including current tick - NO future data
        const currentPrice = currentData.price;
        const priceHistory = bundle.trajectory.slice(0, currentTimeStep + 1).map(d => d.price);
        const momentumHistory = bundle.trajectory.slice(0, currentTimeStep + 1).map(d => d.momentum);

        // Calculate trend from available data
        const priceChange = currentTimeStep > 0 ? ((currentPrice - bundle.trajectory[0].price) / bundle.trajectory[0].price * 100) : 0;
        const avgMomentum = momentumHistory.reduce((a, b) => a + b, 0) / momentumHistory.length;

        // Prepare stats for LLM - ONLY using current/past data
        const statsPrompt = `
Current Bundle Stats (up to this point in time):
- Current Price: $${currentPrice.toFixed(2)}
- Overall Price Change: ${priceChange.toFixed(2)}%
- Current Momentum: ${(currentData.momentum * 100).toFixed(2)}%
- Average Momentum (up to now): ${(avgMomentum * 100).toFixed(2)}%
- Current Volatility: ${(currentData.volatility * 100).toFixed(2)}%
- Performance Score: ${bundle.performanceScore.toFixed(1)}/100

Based ONLY on the information available up to this point (you cannot see future data), predict if the price ${predictionDaysOut} days from now will be UP or DOWN compared to today. Respond with only one word: UP or DOWN`;

        // Call prediction endpoint with reasoning
        const response = await fetch('/api/predict', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: statsPrompt, withReasoning: true }),
        });

        if (!response.ok) throw new Error('Prediction failed');
        const result = await response.json();
        const prediction = result.prediction?.toUpperCase() || 'UP';
        const reasoning = result.reasoning || 'No reasoning provided';

        // Store prediction WITHOUT outcome yet
        setPredictions(prev => ({
          ...prev,
          [currentTimeStep]: {
            prediction,
            reasoning,
            actual: null,
            correct: null,
            targetStep: targetStep,
            predictionDaysOut: predictionDaysOut,
          },
        }));

        // Increment prediction count
        setPredictionCount(prev => prev + 1);

        // Auto-select the newly created prediction to show its details
        setSelectedPredictionStep(currentTimeStep);

        // Auto-progress after 2 seconds to reveal outcome
        setIsAutoProgressing(true);
        setTimeout(() => {
          if (currentTimeStep < totalTimeSteps - 1) {
            setCurrentTimeStep(currentTimeStep + 1);
          }
          setIsAutoProgressing(false);
        }, 2000);
      } catch (err) {
        console.error('Prediction error:', err);
      }
    };

    getPrediction();
  }, [autoProgressEnabled, currentTimeStep, bundle, totalTimeSteps, isAutoProgressing, predictions, predictionCount]);

  const handleStockHover = (stock) => {
    setHoveredStock(stock);
  };

  const handleStockUnhover = () => {
    setHoveredStock(null);
  };

  const handleStockClick = (stock) => {
    setInternalSelectedStock(stock);
  };

  const handleVertexClick = (stepIndex) => {
    // Jump to the clicked step
    setCurrentTimeStep(stepIndex);
    // Request a single prediction for this step (not auto-progressing)
    setRequestedPredictionStep(stepIndex);
  };

  // Generate bundle-specific market research based on stocks in bundle
  const generateBundleResearch = () => {
    // Use API data if available
    if (bundleAnalysis?.marketResearch) {
      return bundleAnalysis.marketResearch.map(article => article.title);
    }

    // Fallback to empty array while loading
    return [];
  };

  // Analyze bundle strengths and weaknesses across timeframes
  const getBundleAnalysis = () => {
    // Return API data if available
    if (bundleAnalysis) {
      return bundleAnalysis;
    }

    // Return null while loading or if no bundle
    return null;
  };

  return (
    <div className="stock-universe-container">
      {/* Axis labels */}
      <div className="axis-label axis-label-x">TIME →</div>
      <div className="axis-label axis-label-y">PRICE ↑</div>

      {/* Y-axis scale (Price) */}
      {validPriceRange && (
        <div className="axis-scale y-axis-scale">
          <div className="y-tick">${maxPrice.toFixed(2)}</div>
          <div className="y-tick">${((minPrice + maxPrice) / 2).toFixed(2)}</div>
          <div className="y-tick">${minPrice.toFixed(2)}</div>
        </div>
      )}

      {/* X-axis scale (Time) */}
      <div className="axis-scale x-axis-scale">
        <div className="x-tick">0</div>
        <div className="x-tick">25</div>
        <div className="x-tick">50</div>
        <div className="x-tick">75</div>
        <div className="x-tick">100</div>
      </div>

      {/* Back button */}
      {onBack && (
        <button className="back-button" onClick={onBack} title="Back to selector">
          ← Back
        </button>
      )}

      {/* Timeframe selector */}
      <div className="timeframe-selector">
        <span className="timeframe-label">TIMEFRAME:</span>
        <div className="timeframe-buttons">
          {timeframeOptions.map(option => (
            <button
              key={option.value}
              className={`timeframe-btn ${selectedTimeframe === option.value ? 'active' : ''}`}
              onClick={() => setSelectedTimeframe(option.value)}
              title={`View ${option.label} data`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <Canvas orthographic camera={{ position: [0, 0, 5], zoom: 40, near: 0.1, far: 100 }}>
        <Suspense fallback={null}>
          {/* Lighting */}
          <ambientLight intensity={0.6} />
          <pointLight position={[10, 10, 10]} intensity={0.8} />
          <pointLight position={[-10, -10, 10]} intensity={0.4} color="#ff00ff" />

          {/* Grid helper - shows coordinate system */}
          <gridHelper args={[20, 20, '#444444', '#222222']} position={[0, 0, -0.5]} />

          {/* Bundle trails (history path) */}
          {showTrails && displayStocks.map((stock) => (
            <StockTrail
              key={`trail-${stock.id}`}
              stock={stock}
              timeStep={currentTimeStep}
              showTrails={showTrails}
            />
          ))}

          {/* Bundle particle */}
          {displayStocks.map((stock) => (
            <StockParticle
              key={stock.id}
              stock={stock}
              timeStep={currentTimeStep}
              onHover={handleStockHover}
              onUnhover={handleStockUnhover}
              onClick={handleStockClick}
            />
          ))}

          {/* Trajectory vertices - clickable points at each tick */}
          {displayStocks.map((stock) => (
            <TrajectoryVertices
              key={`vertices-${stock.id}`}
              stock={stock}
              onVertexClick={handleVertexClick}
              selectedStep={currentTimeStep}
            />
          ))}

          {/* Camera controls handler */}
          <CameraController />
        </Suspense>
      </Canvas>

      {/* Right Sidebar */}
      <div style={{
        position: 'fixed',
        right: 20,
        top: 70,
        bottom: 120,
        width: 280,
        background: 'rgba(0, 0, 0, 0.85)',
        border: '2px solid #00ffff',
        borderRadius: '8px',
        padding: '16px',
        fontFamily: "'Courier New', monospace",
        fontSize: '12px',
        color: '#00ff00',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        scrollBehavior: 'smooth',
      }}>
        {/* Bundle Stats - At top */}
        {updatedBundleData && (
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ color: '#00ffff', margin: '0 0 8px 0', fontSize: '13px', borderBottom: '1px solid rgba(0, 255, 255, 0.3)', paddingBottom: '6px' }}>📊 Bundle</h3>
            <div style={{ fontSize: '11px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#888' }}>Stocks:</span>
                <span style={{ color: '#00ffff' }}>{updatedBundleData?.name || 'Loading...'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#888' }}>Price:</span>
                <span style={{ color: '#00ffff' }}>${(updatedBundleData?.price || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#888' }}>Change:</span>
                <span style={{ color: (updatedBundleData?.priceChange || 0) > 0 ? '#00ff00' : '#ff4444' }}>
                  {(updatedBundleData?.priceChange || 0) > 0 ? '+' : ''}
                  {(updatedBundleData?.priceChange || 0).toFixed(2)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#888' }}>Momentum:</span>
                <span style={{ color: (updatedBundleData?.momentum || 0) > 0 ? '#00ff00' : '#ff4444' }}>
                  {(updatedBundleData?.momentum || 0) > 0 ? '↑' : '↓'} {Math.abs((updatedBundleData?.momentum || 0) * 100).toFixed(2)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#888' }}>Volatility:</span>
                <span style={{ color: '#00ffff' }}>{((updatedBundleData?.volatility || 0) * 100).toFixed(2)}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#888' }}>Performance:</span>
                <span style={{ color: '#ffff00' }}>{((updatedBundleData?.performanceScore || 0).toFixed(1))}/100</span>
              </div>
              <div style={{ paddingTop: '6px', borderTop: '1px solid rgba(0, 255, 255, 0.2)', marginBottom: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '2px' }}>
                  <span style={{ color: '#888' }}>Prob ↑:</span>
                  <span style={{ color: '#00ff00' }}>{((updatedBundleData?.probUp || 0).toFixed(1))}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Prob ↓:</span>
                  <span style={{ color: '#ff4444' }}>{((updatedBundleData?.probDown || 0).toFixed(1))}%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls Section */}
        <div style={{ marginBottom: '12px', borderTop: '1px solid rgba(0, 255, 0, 0.2)', paddingTop: '8px' }}>
          <strong style={{ color: '#00ffff', fontSize: '12px' }}>Controls</strong>
          <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#00ff00', fontSize: '11px' }}>
            <li>Pan: Left click & drag ↕</li>
            <li>Hover: See details</li>
          </ul>
        </div>

        {/* Chart Guide */}
        <div style={{ marginBottom: '12px', borderTop: '1px solid rgba(0, 255, 0, 0.2)', paddingTop: '8px' }}>
          <strong style={{ color: '#00ffff', fontSize: '12px' }}>Chart Guide</strong>
          <div style={{ fontSize: '11px', marginTop: '4px' }}>
            <div><span style={{ color: '#FF4444' }}>←→</span> <span style={{ color: '#888' }}>Time</span></div>
            <div><span style={{ color: '#44FF44' }}>↑↓</span> <span style={{ color: '#888' }}>Price</span></div>
            <div><span style={{ color: '#00ffff' }}>↑/↓</span> <span style={{ color: '#888' }}>Momentum</span></div>
            <div><span style={{ color: '#888' }}>Size = Volume</span></div>
          </div>
        </div>

        {/* LLM Prediction Section */}
        <div style={{ marginBottom: '12px', borderTop: '1px solid rgba(0, 255, 0, 0.2)', paddingTop: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#00ffff', fontSize: '12px', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={autoProgressEnabled}
              onChange={(e) => {
                setAutoProgressEnabled(e.target.checked);
                setPredictions({});
                setPredictionCount(0);
              }}
              style={{ marginRight: '8px', cursor: 'pointer', accentColor: '#00ffff' }}
            />
            <span>LLM Predict 🤖 ({predictionCount}/2)</span>
          </label>

          {autoProgressEnabled && Object.keys(predictions).length > 0 && (
            <div style={{ fontSize: '11px' }}>
              {/* Selected Prediction Analysis - Shows first when a prediction is selected */}
              {selectedPredictionStep !== null && predictions[selectedPredictionStep] && bundle && (
                <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0, 255, 255, 0.2)', backgroundColor: 'rgba(0, 255, 255, 0.05)', padding: '8px', borderRadius: '4px' }}>
                  <div style={{ color: '#ffff00', fontSize: '11px', fontWeight: 'bold', marginBottom: '6px' }}>
                    📋 Step {selectedPredictionStep} (2-day forecast):
                  </div>

                  {/* Prediction and Reasoning */}
                  <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(0, 255, 255, 0.15)' }}>
                    <div style={{ color: '#00ffff', fontSize: '10px', lineHeight: '1.4', fontStyle: 'italic' }}>
                      "{predictions[selectedPredictionStep].reasoning}"
                    </div>
                    {predictions[selectedPredictionStep].actual !== null && (
                      <div style={{ color: '#888', fontSize: '10px', marginTop: '4px', paddingTop: '4px' }}>
                        Actual: <span style={{ color: predictions[selectedPredictionStep].actual === 'UP' ? '#00ff00' : '#ff4444', fontWeight: 'bold' }}>
                          {predictions[selectedPredictionStep].actual === 'UP' ? '↑ UP' : '↓ DOWN'}
                        </span>
                        <span style={{ marginLeft: '8px', color: predictions[selectedPredictionStep].correct ? '#ffff00' : '#ff6b6b' }}>
                          {predictions[selectedPredictionStep].correct ? '✓ Correct' : '✗ Wrong'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Analysis Parameters */}
                  <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(0, 255, 255, 0.15)' }}>
                    <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>📊 Parameters:</div>
                    <div style={{ fontSize: '9px', color: '#00ff00', lineHeight: '1.3' }}>
                      <div>• Momentum: {(bundle.trajectory[selectedPredictionStep]?.momentum * 100).toFixed(2)}%</div>
                      <div>• Volatility: {(bundle.trajectory[selectedPredictionStep]?.volatility * 100).toFixed(2)}%</div>
                      <div>• Price Trend: {((bundle.trajectory[selectedPredictionStep]?.price - bundle.trajectory[0]?.price) / bundle.trajectory[0]?.price * 100).toFixed(2)}%</div>
                      <div>• Volume: {(bundle.trajectory[selectedPredictionStep]?.volume / 1000000).toFixed(1)}M</div>
                    </div>
                  </div>

                  {/* Simulated Articles/News */}
                  <div>
                    <div style={{ color: '#888', fontSize: '10px', fontWeight: 'bold', marginBottom: '3px' }}>📰 News/Context:</div>
                    <div style={{ fontSize: '9px', color: '#ffaa00', lineHeight: '1.3' }}>
                      <div>• Market showing {bundle.trajectory[selectedPredictionStep]?.momentum > 0 ? 'positive' : 'negative'} momentum</div>
                      <div>• Volatility: {bundle.trajectory[selectedPredictionStep]?.volatility > 0.05 ? 'high' : 'moderate'}</div>
                      <div>• Fed policy favors equity markets</div>
                      <div>• Tech sector rotation in progress</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Current tick prediction */}
              {predictions[currentTimeStep] && !selectedPredictionStep && (
                <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0, 255, 255, 0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#ffff00', fontWeight: 'bold', fontSize: '11px' }}>🤖 AI Prediction:</span>
                    <span style={{ color: predictions[currentTimeStep].prediction === 'UP' ? '#00ff00' : '#ff4444', fontWeight: 'bold' }}>
                      {predictions[currentTimeStep].prediction === 'UP' ? '↑ UP' : '↓ DOWN'}
                    </span>
                  </div>

                  {/* Reasoning */}
                  <div style={{ backgroundColor: 'rgba(0, 255, 255, 0.08)', padding: '6px', borderRadius: '4px', marginBottom: '6px' }}>
                    <div style={{ color: '#00ffff', fontSize: '9px', lineHeight: '1.4', fontStyle: 'italic' }}>
                      "{predictions[currentTimeStep].reasoning}"
                    </div>
                  </div>

                  {/* Result if available */}
                  {predictions[currentTimeStep].actual !== null && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '10px' }}>
                        <span style={{ color: '#888' }}>Actual:</span>
                        <span style={{ color: predictions[currentTimeStep].actual === 'UP' ? '#00ff00' : '#ff4444', fontWeight: 'bold' }}>
                          {predictions[currentTimeStep].actual === 'UP' ? '↑ UP' : '↓ DOWN'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                        <span style={{ color: '#888' }}>Result:</span>
                        <span style={{ color: predictions[currentTimeStep].correct ? '#ffff00' : '#ff6b6b', fontWeight: 'bold' }}>
                          {predictions[currentTimeStep].correct ? '✓ Correct!' : '✗ Wrong'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Prediction Markers - clickable circles */}
              <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(0, 255, 255, 0.2)' }}>
                <div style={{ color: '#888', fontSize: '10px', marginBottom: '4px' }}>📍 Predictions (click to review):</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {Object.keys(predictions).map(stepStr => {
                    const step = parseInt(stepStr);
                    const pred = predictions[step];
                    const isSelected = selectedPredictionStep === step;
                    const isCorrect = pred.correct === true;
                    const isWrong = pred.correct === false;

                    return (
                      <button
                        key={step}
                        onClick={() => setSelectedPredictionStep(isSelected ? null : step)}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          border: isSelected ? '2px solid #ffff00' : '1px solid rgba(0, 255, 255, 0.5)',
                          background: isCorrect ? 'rgba(0, 255, 0, 0.2)' : isWrong ? 'rgba(255, 68, 68, 0.2)' : 'rgba(0, 255, 255, 0.1)',
                          color: isSelected ? '#ffff00' : '#00ffff',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          fontFamily: "'Courier New', monospace",
                        }}
                        title={`Step ${step}: ${pred.prediction} ${isCorrect ? '✓' : isWrong ? '✗' : ''}`}
                      >
                        {step}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Accuracy Stats */}
              <div style={{ paddingTop: '6px', borderTop: '1px solid rgba(0, 255, 255, 0.2)', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px' }}>
                  <span style={{ color: '#888' }}>Accuracy:</span>
                  <span style={{ color: '#ffff00' }}>
                    {Math.round((Object.values(predictions).filter(p => p.correct !== null && p.correct).length / Object.values(predictions).filter(p => p.correct !== null).length || 0) * 100)}%
                  </span>
                </div>
              </div>

              {/* Bundle Market Research */}
              <div style={{ borderTop: '1px solid rgba(0, 255, 255, 0.2)', paddingTop: '8px', marginBottom: '8px' }}>
                <div style={{ color: '#ffff00', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>📰 Market Research</div>
                <div style={{ fontSize: '9px', color: '#ffaa00', lineHeight: '1.5' }}>
                  {analyzingBundle ? (
                    <div style={{ color: '#ffaa00' }}>Analyzing bundle...</div>
                  ) : bundleAnalysis?.marketResearch ? (
                    bundleAnalysis.marketResearch.slice(0, 4).map((article, idx) => (
                      <div key={idx} style={{ marginBottom: '4px', paddingBottom: '4px', borderBottom: idx < 3 ? '1px solid rgba(255, 170, 0, 0.2)' : 'none' }}>
                        • {article.title}
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#ffaa00' }}>No research available</div>
                  )}
                </div>
              </div>

              {/* Bundle Analysis - Strengths & Weaknesses */}
              {bundleAnalysis && (
                <div style={{ borderTop: '1px solid rgba(0, 255, 255, 0.2)', paddingTop: '8px' }}>
                  <div style={{ color: '#ffff00', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>🎯 Bundle Analysis</div>

                  {/* Short Term */}
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#ffaa00', fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>Short Term:</div>
                    <div style={{ fontSize: '8px', color: '#00ff00', marginLeft: '8px' }}>
                      {bundleAnalysis.shortTerm?.strengths?.map((s, i) => (
                        <div key={`st-s-${i}`}>✓ {s}</div>
                      ))}
                    </div>
                    <div style={{ fontSize: '8px', color: '#ff6b6b', marginLeft: '8px' }}>
                      {bundleAnalysis.shortTerm?.weaknesses?.map((w, i) => (
                        <div key={`st-w-${i}`}>✗ {w}</div>
                      ))}
                    </div>
                  </div>

                  {/* Medium Term */}
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#ffaa00', fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>Medium Term:</div>
                    <div style={{ fontSize: '8px', color: '#00ff00', marginLeft: '8px' }}>
                      {bundleAnalysis.mediumTerm?.strengths?.map((s, i) => (
                        <div key={`mt-s-${i}`}>✓ {s}</div>
                      ))}
                    </div>
                    <div style={{ fontSize: '8px', color: '#ff6b6b', marginLeft: '8px' }}>
                      {bundleAnalysis.mediumTerm?.weaknesses?.map((w, i) => (
                        <div key={`mt-w-${i}`}>✗ {w}</div>
                      ))}
                    </div>
                  </div>

                  {/* Long Term */}
                  <div>
                    <div style={{ color: '#ffaa00', fontSize: '9px', fontWeight: 'bold', marginBottom: '2px' }}>Long Term:</div>
                    <div style={{ fontSize: '8px', color: '#00ff00', marginLeft: '8px' }}>
                      {bundleAnalysis.longTerm?.strengths?.map((s, i) => (
                        <div key={`lt-s-${i}`}>✓ {s}</div>
                      ))}
                    </div>
                    <div style={{ fontSize: '8px', color: '#ff6b6b', marginLeft: '8px' }}>
                      {bundleAnalysis.longTerm?.weaknesses?.map((w, i) => (
                        <div key={`lt-w-${i}`}>✗ {w}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trails Toggle */}
        <div style={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)', paddingTop: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#00ffff', fontSize: '12px' }}>
            <input
              type="checkbox"
              checked={showTrails}
              onChange={(e) => setShowTrails(e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer', accentColor: '#00ffff' }}
            />
            <span>Show Trails ✨</span>
          </label>
        </div>
      </div>

      {/* Timeline Player */}
      <TimelinePlayer
        totalSteps={totalTimeSteps}
        currentStep={currentTimeStep}
        onStepChange={setCurrentTimeStep}
        stocks={displayStocks}
      />

      {/* Loading Indicator */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading stock data...</p>
          </div>
        </div>
      )}

      {/* Error Indicator */}
      {error && (
        <div className="error-overlay">
          <div className="error-message">
            <p>⚠ Error loading data: {error}</p>
            <p>Using sample data</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockUniverse;
